import { Intro, DataTable, Pill, PopOverMenu, Text } from 'components/commons';
import { useApi, useFilter, useMount } from 'hooks';
import useModal from 'hooks/useModal';
import React, { useCallback, useContext, useMemo } from 'react';
import { dateTimeFormat, formatCurrency, formatDate, prettifyCustomerStatus } from 'utils';

import styles from './customer-account.module.scss';
import CustomerStatus from 'enums/driver-status';
import { getCustomerClaims } from 'apis/customer.api';

import { DateTime } from 'enums';
import { UserContext } from 'contexts';
import CustomerAccountClaimsConfirmationModal from './customer-account-claims-confirmation-modal';
import CustomerClaimsFilter from './customer-claims-filter';
import { customerClaimsColumns } from './customer-claims-columns';
import { prettifyRequestAction } from 'utils/pretty.utils';
import { customerClaimsFilterState } from './customer-claims-filter.state';

const CustomerClaimsModule = () => {
  const user = useContext(UserContext);

  const statusModal = useModal();

  const { modifyFilter, modifyFilters, filterState, requestState, submitFilter } = useFilter(customerClaimsFilterState());

  const {
    request: getCustomerClaimsRequest,
    loading: fetchingCustomerClaims,
    result: getCustomerClaimsResult = { customerClaims: [], count: 0 }
  } = useApi({
    api: getCustomerClaims,
    pageError: true
  });

  useMount(() => {
    fetchCustomers(requestState);
  });

  const fetchCustomers = useCallback(
    (rs) => {
      getCustomerClaimsRequest(submitFilter({ ...rs }));
    },
    [getCustomerClaimsRequest, submitFilter]
  );

  const changeStatusCb = useCallback(
    (interestAmount, customerClaimId, status, requestAction) => {
      statusModal.show({ interestAmount, customerClaimId, status, requestAction });
    },
    [statusModal]
  );

  const preparedCustomerClaimsData = useMemo(() => {
    const { customerClaims } = getCustomerClaimsResult;
    if (customerClaims.length > 0) {
      const preparedData = customerClaims.map((c) => {
        const map = new Map();

        map.set(
          'name',
          <div>
            <Text>
              {c.customer.firstName} {c.customer.lastName}
            </Text>
            <Text className={styles.subBusinessId}>{c.customerAccount.customerAccountNumber}</Text>
          </div>
        );

        map.set(
          'product',
          <div>
            <Pill cement={c.productId === 1} cheddar={c.productId === 2} sky={c.productId === 3} grass={c.productId === 4}>
              {c.productName}
            </Pill>
          </div>
        );
        map.set();
        map.set('periodYear', c?.periodYear || '--');
        map.set('principalAmount', formatCurrency(c?.principalAmount || 0));
        map.set('interestAmount', formatCurrency(c?.interestAmount || 0));
        map.set('interest', c?.interest || 0);
        map.set(
          'status',
          <>
            <Pill
              grass={c.status === CustomerStatus.Claimed}
              sky={c.status === CustomerStatus.Pending}
              deepRed={c.status === CustomerStatus.Rejected}
            >
              {prettifyCustomerStatus(c.status)}
            </Pill>
            <div className={styles.subBusinessId}>
              {c.status === CustomerStatus.Claimed && formatDate(c.claimedAt, DateTime.T)}
              {c.status === CustomerStatus.Rejected && c.rejectedAt && formatDate(c.rejectedAt, DateTime.T)}
              {c.status === CustomerStatus.Pending && c.requestedAt && formatDate(c.requestedAt, DateTime.T)}
            </div>
          </>
        );
        map.set(
          'approvalStatus',
          <>
            <b>{prettifyRequestAction(c.requestAction)}</b>
            <div className={styles.subBusinessId}>
              {c.status === CustomerStatus.Rejected && c.rejectedAt && formatDate(c.rejectedAt, DateTime.T)}
              {c.status === CustomerStatus.Pending && c.requestedAt && formatDate(c.requestedAt, DateTime.T)}
            </div>
          </>
        );

        map.set('createdAt', dateTimeFormat(c.createdAt));
        const options = [];

        if (c.status === CustomerStatus.Pending) {
          if (['super-admin', 'maker'].includes(user?.role) && !c.requestAction) {
            options.push({
              content: 'Transfer to Savings',
              onClick: () => {
                changeStatusCb(c.interestAmount, c.customerClaimId, c.status, 'transfer-savings');
              }
            });
          } else if (['admin', 'approver'].includes(user?.role) && c.requestAction) {
            options.push({
              content: 'Approve',
              onClick: () => {
                changeStatusCb(c.interestAmount, c.customerClaimId, 'approve', c.requestAction);
              }
            });
            options.push({
              content: 'Reject',
              onClick: () => {
                changeStatusCb(c.interestAmount, c.customerClaimId, 'reject', c.requestAction);
              }
            });
          }
        }

        if (options && options.length > 0) {
          map.set('options', <PopOverMenu options={options} />);
        }

        return map;
      });
      return preparedData;
    }
    return [];
  }, [changeStatusCb, getCustomerClaimsResult, user?.role]);

  const onStatusChangeCb = useCallback(
    (status) => {
      const { requestState } = modifyFilters({ status, page: 1 });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onMonthChangeCb = useCallback(
    (month) => {
      const { requestState } = modifyFilters({ month, page: 1 });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onYearChangeCb = useCallback(
    (year) => {
      const { requestState } = modifyFilters({ year, page: 1 });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onProductChangeCb = useCallback(
    (product) => {
      const { requestState } = modifyFilters({ product, productId: product.value, page: 1 });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onSearchCb = useCallback(
    (searchKey) => {
      const { requestState } = modifyFilters({ searchKey, page: 1 });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onChangePageCb = useCallback(
    (page) => {
      const { requestState } = modifyFilters({ page });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  const onChangePageSizeCb = useCallback(
    (perPage) => {
      const { requestState } = modifyFilters({ perPage, page: 1 });
      fetchCustomers(requestState);
    },
    [fetchCustomers, modifyFilters]
  );

  return (
    <div>
      <div className={styles.intro}>
        <Intro title={'Claims History'} subtitle={`Manage your members' account claims`} action />
      </div>
      <div className={styles.filters}>
        <div>
          <CustomerClaimsFilter
            filterState={filterState}
            onStatusChange={onStatusChangeCb}
            onYearChange={onYearChangeCb}
            onMonthChange={onMonthChangeCb}
            onProductChange={onProductChangeCb}
            onSearchChange={modifyFilter}
            onSearch={onSearchCb}
          />
        </div>
      </div>
      <div className={styles.table}>
        <DataTable
          loading={fetchingCustomerClaims}
          page={filterState.page}
          pageSize={filterState.perPage}
          columns={customerClaimsColumns}
          data={preparedCustomerClaimsData}
          dataCount={getCustomerClaimsRequest.count}
          onChangePage={onChangePageCb}
          onChangePageSize={onChangePageSizeCb}
        />
      </div>

      <CustomerAccountClaimsConfirmationModal refetchCustomerClaims={() => fetchCustomers(requestState)} {...statusModal} />
    </div>
  );
};

export default CustomerClaimsModule;
